<div class="complus-admin container mt-5">
  <h3 class="title mb-1">{{ 'complus-admin.title' | translate }}</h3>
  <p class="mb-4">{{ 'complus-admin.description' | translate }}</p>

  @if (loading) {
    <p-skeleton height="160px"></p-skeleton>
  } @else {
    @if (access?.isVendor) {
      @if (customerAccessActive) {
        <div class="complus-alert complus-alert--danger mb-4" role="alert">
          <div>
            <strong>{{ 'complus-admin.customer-access.enabled_title' | translate }}</strong>
            <p class="mb-0">
              {{ 'complus-admin.customer-access.enabled_description' | translate: { command: secretCommand } }}
            </p>
            <p class="mb-0">
              {{
                'complus-admin.customer-access.expires_at'
                  | translate: { time: (access.customerAccessExpiresAt | date: 'HH:mm') }
              }}
            </p>
          </div>
          <span class="complus-alert-actions">
            <ng-casto-button
              class="secondary"
              [label]="'complus-admin.customer-access.extend' | translate"
              [loading]="togglingCustomerAccess"
              (click)="toggleCustomerAccess(true)">
            </ng-casto-button>
            <ng-casto-button
              class="primary"
              [label]="'complus-admin.customer-access.disable' | translate"
              [loading]="togglingCustomerAccess"
              (click)="toggleCustomerAccess(false)">
            </ng-casto-button>
          </span>
        </div>
      } @else {
        <div class="complus-alert complus-alert--info mb-4">
          <div>
            <strong>{{ 'complus-admin.customer-access.disabled_title' | translate }}</strong>
            <p class="mb-0">
              {{ 'complus-admin.customer-access.disabled_description' | translate: { command: secretCommand } }}
            </p>
          </div>
          <ng-casto-button
            class="secondary"
            [label]="'complus-admin.customer-access.enable' | translate"
            [loading]="togglingCustomerAccess"
            (click)="toggleCustomerAccess(true)">
          </ng-casto-button>
        </div>
      }
    } @else {
      <div class="complus-alert complus-alert--danger mb-4" role="alert">
        <div>
          <strong>{{ 'complus-admin.customer-access.customer_site_title' | translate }}</strong>
          @if (customerAccessActive) {
            <p class="mb-0">
              {{
                'complus-admin.customer-access.customer_site_description'
                  | translate: { time: (access?.customerAccessExpiresAt | date: 'HH:mm') }
              }}
            </p>
          } @else {
            <p class="mb-0">{{ 'complus-admin.customer-access.expired' | translate }}</p>
          }
        </div>
      </div>
    }

    <form class="complus-card" [formGroup]="form" (ngSubmit)="generate()">
      <h4 class="section-title">{{ 'complus-admin.header.title' | translate }}</h4>
      <div class="complus-grid mb-4">
        <label>
          {{ 'complus-admin.header.customer_id' | translate }} *
          <input
            class="form-control"
            type="text"
            formControlName="customerId"
            placeholder="SQ_3001836829"
            (blur)="normalizeCustomerId()"
            [class.is-invalid]="isInvalid(form.controls.customerId)" />
        </label>
        <label>
          {{ 'complus-admin.header.vendor_id' | translate }}
          <input class="form-control" type="text" formControlName="vendorId" />
        </label>
        <label>
          {{ 'complus-admin.header.store_id' | translate }} *
          <input
            class="form-control"
            type="text"
            formControlName="storeId"
            placeholder="1410"
            [class.is-invalid]="isInvalid(form.controls.storeId)" />
        </label>
        <label>
          {{ 'complus-admin.header.project_name' | translate }}
          <input class="form-control" type="text" formControlName="projectName" />
        </label>
        <label>
          {{ 'complus-admin.header.component_id' | translate }} *
          <input
            class="form-control"
            type="text"
            formControlName="componentId"
            [class.is-invalid]="isInvalid(form.controls.componentId)" />
        </label>
        <label>
          {{ 'complus-admin.header.type' | translate }} *
          <input
            class="form-control"
            type="text"
            formControlName="type"
            [class.is-invalid]="isInvalid(form.controls.type)" />
        </label>
        <label>
          {{ 'complus-admin.header.prefix' | translate }}
          <input class="form-control" type="text" formControlName="projectReferencePrefix" />
        </label>
        <label>
          {{ 'complus-admin.header.contacted_by_phone' | translate }}
          <select class="form-control" formControlName="contactedByPhone">
            <option value="none">{{ 'complus-admin.header.contacted_by_phone_none' | translate }}</option>
            <option value="true">{{ 'generic.yes' | translate }}</option>
            <option value="false">{{ 'generic.no' | translate }}</option>
          </select>
        </label>
      </div>

      <h4 class="section-title">{{ 'complus-admin.lines.title' | translate }}</h4>
      <div class="complus-lines" formArrayName="lines">
        <div class="complus-line complus-line--head">
          <span>#</span>
          <span>{{ 'complus-admin.lines.ean' | translate }} *</span>
          <span>{{ 'complus-admin.lines.description' | translate }}</span>
          <span>{{ 'complus-admin.lines.quantity' | translate }} *</span>
          <span>{{ 'complus-admin.lines.unit_price' | translate }} *</span>
          <span>{{ 'complus-admin.lines.note' | translate }}</span>
          <span></span>
        </div>
        @for (line of lines.controls; track line; let i = $index) {
          <div class="complus-line" [formGroupName]="i">
            <span class="complus-line-number">{{ i + 1 }}</span>
            <input
              class="form-control"
              type="text"
              inputmode="numeric"
              formControlName="ean"
              [attr.aria-label]="'complus-admin.lines.ean' | translate"
              [class.is-invalid]="isInvalid(line.controls.ean)" />
            <input
              class="form-control"
              type="text"
              formControlName="description"
              maxlength="100"
              [attr.aria-label]="'complus-admin.lines.description' | translate" />
            <input
              class="form-control"
              type="number"
              min="0.01"
              step="any"
              formControlName="quantity"
              [attr.aria-label]="'complus-admin.lines.quantity' | translate"
              [class.is-invalid]="isInvalid(line.controls.quantity)" />
            <input
              class="form-control"
              type="number"
              min="0"
              step="0.01"
              formControlName="unitPrice"
              [attr.aria-label]="'complus-admin.lines.unit_price' | translate"
              [class.is-invalid]="isInvalid(line.controls.unitPrice)" />
            <input
              class="form-control"
              type="text"
              formControlName="note"
              maxlength="500"
              [attr.aria-label]="'complus-admin.lines.note' | translate" />
            <span class="complus-line-actions">
              <button
                type="button"
                class="complus-icon-button"
                (click)="duplicateLine(i)"
                [title]="'generic.duplicate' | translate">
                <fa-icon [icon]="faCopy"></fa-icon>
              </button>
              <button
                type="button"
                class="complus-icon-button"
                (click)="removeLine(i)"
                [title]="'generic.delete' | translate">
                <fa-icon [icon]="faTrash"></fa-icon>
              </button>
            </span>
          </div>
        }
      </div>
      <div class="complus-lines-footer mb-4">
        <ng-casto-button
          class="secondary"
          [faIcon]="faPlus"
          [label]="'complus-admin.lines.add' | translate"
          (click)="addLine()"></ng-casto-button>
        <span class="complus-total">
          {{ 'complus-admin.lines.total' | translate }} : <strong>{{ total | number: '1.2-2' }} €</strong>
        </span>
      </div>

      <div class="complus-actions">
        <ng-casto-button
          class="primary"
          [label]="'complus-admin.xml.generate' | translate"
          [loading]="generating"
          (click)="generate()"></ng-casto-button>
        @if (form.invalid && form.touched) {
          <span class="complus-error">{{ 'complus-admin.xml.invalid_form' | translate }}</span>
        }
      </div>
      @if (generationError) {
        <p class="complus-error mt-3" role="alert">{{ generationError }}</p>
      }
    </form>

    @if (xml) {
      <section class="complus-card mt-4">
        <div class="complus-xml-header">
          <h4 class="section-title mb-0">{{ 'complus-admin.xml.title' | translate }}</h4>
          <span class="complus-xml-tools">
            <ng-casto-button
              class="secondary"
              [faIcon]="faCopy"
              [label]="'complus-admin.xml.copy' | translate"
              (click)="copyXml()"></ng-casto-button>
            <ng-casto-button
              class="secondary"
              [faIcon]="faDownload"
              [label]="'generic.download' | translate"
              (click)="downloadXml()"></ng-casto-button>
          </span>
        </div>
        <p class="complus-hint">{{ 'complus-admin.xml.editable_hint' | translate }}</p>
        <textarea
          class="form-control complus-xml"
          spellcheck="false"
          [(ngModel)]="xml"
          [attr.aria-label]="'complus-admin.xml.title' | translate"></textarea>

        <h4 class="section-title mt-4">{{ 'complus-admin.send.title' | translate }}</h4>
        <div class="complus-send">
          <ng-casto-button
            class="primary"
            [faIcon]="faPaperPlane"
            [label]="'complus-admin.send.submit' | translate"
            [loading]="sending"
            (click)="send()"></ng-casto-button>
        </div>

        @if (sendResult) {
          <div class="complus-alert complus-alert--success mt-3" role="status">
            <div>
              <strong>{{ 'complus-admin.send.success' | translate }}</strong>
              @if (sendResult.opportunityNumber) {
                <p class="mb-0">
                  {{ 'complus-admin.send.opportunity_number' | translate }} :
                  <strong>{{ sendResult.opportunityNumber }}</strong>
                </p>
              }
            </div>
          </div>
          <pre class="complus-response">{{ sendResult.response }}</pre>
        }
        @if (sendError) {
          <div class="complus-alert complus-alert--danger mt-3" role="alert">
            <div>
              <strong>{{ 'complus-admin.send.error' | translate }}</strong>
              <pre class="complus-response mb-0">{{ sendError }}</pre>
            </div>
          </div>
        }
      </section>
    }
  }
</div>
